import { useMemo, useState } from 'react';
import { useFavorites } from '@/hooks/useFavorites';
import { favoriteTypeMeta, type CoreFavorite } from '@/lib/coreFavorites';
import { DEFAULT_STORE_LOGO, storeLogoOr } from '@/lib/defaultImages';

interface Meta {
  image?: string | null;
  ville?: string | null;
  price?: number | null;
  currency?: string | null;
  boutiqueName?: string | null;
  resultType?: string | null;
}

function readMeta(f: CoreFavorite): Meta {
  const m = (f.metadata || {}) as Meta;
  return m;
}

const TYPE_FILTERS: { key: string; label: string; icon: string }[] = [
  { key: 'all', label: 'Tout', icon: 'ri-apps-2-line' },
  { key: 'product', label: 'Produits', icon: 'ri-shopping-bag-3-line' },
  { key: 'service', label: 'Services', icon: 'ri-service-line' },
  { key: 'business', label: 'Commerces', icon: 'ri-store-2-line' },
];

/**
 * Onglet « Mes favoris » du compte client.
 * Regroupe tout ce que le client a mis en favori — depuis le chat IA ou ailleurs.
 */
export default function FavoritesTab() {
  const { favorites, remove, loading, error, reload } = useFavorites();
  const [filter, setFilter] = useState('all');
  const [removing, setRemoving] = useState<number | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: favorites.length };
    favorites.forEach((f) => { c[f.entity_type] = (c[f.entity_type] || 0) + 1; });
    return c;
  }, [favorites]);

  const visible = useMemo(
    () => (filter === 'all' ? favorites : favorites.filter((f) => f.entity_type === filter)),
    [favorites, filter],
  );

  const handleRemove = async (id: number) => {
    setRemoving(id);
    try { await remove(id); } finally { setRemoving(null); }
  };

  return (
    <div className="max-w-4xl xl:max-w-5xl mx-auto">
      <div className="mb-5">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-heart-3-line text-red-500"></i>
          Mes favoris
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Retrouvez ici tout ce que vous avez enregistré d&apos;un clic, notamment depuis l&apos;assistant IA.
        </p>
      </div>

      {/* Filtres par type */}
      {favorites.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {TYPE_FILTERS.map((t) => {
            const count = counts[t.key] || 0;
            if (t.key !== 'all' && count === 0) return null;
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className={t.icon}></i>
                {t.label}
                <span className={`px-1.5 rounded-full text-[10px] ${active ? 'bg-background-50/25' : 'bg-background-50 text-foreground-500'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/40 rounded-xl text-center">
          <i className="ri-error-warning-line text-2xl text-red-400 mb-2"></i>
          <p className="text-sm text-foreground-600 mb-4">{error}</p>
          <button
            onClick={reload}
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/40 rounded-xl text-center px-4">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-heart-3-line text-2xl text-red-400"></i>
          </div>
          <h3 className="text-foreground-800 font-semibold text-sm mb-1">
            {favorites.length === 0 ? 'Aucun favori pour le moment' : 'Aucun favori dans cette catégorie'}
          </h3>
          <p className="text-foreground-400 text-xs">
            Ouvrez l&apos;assistant IA et touchez le cœur sur un produit, un service ou un commerce pour l&apos;enregistrer ici.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((f) => {
            const meta = readMeta(f);
            const typeMeta = favoriteTypeMeta(f.entity_type);
            const image = meta.image || DEFAULT_STORE_LOGO;
            const price = typeof meta.price === 'number' && meta.price > 0
              ? `${meta.price.toLocaleString('fr-FR')} ${meta.currency || ''}`.trim()
              : null;
            return (
              <div key={f.id} className="bg-background-50 border border-background-200/40 rounded-xl overflow-hidden flex flex-col">
                <div className="relative h-32 bg-background-100 overflow-hidden">
                  <img
                    src={storeLogoOr(image)}
                    alt={f.label || ''}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      if (img.src !== DEFAULT_STORE_LOGO) img.src = DEFAULT_STORE_LOGO;
                    }}
                  />
                  <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${typeMeta.className}`}>
                    <i className={typeMeta.icon}></i>
                    {typeMeta.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemove(f.id)}
                    disabled={removing === f.id}
                    title="Retirer des favoris"
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-background-50/90 flex items-center justify-center cursor-pointer hover:bg-background-50 transition-colors disabled:opacity-50"
                  >
                    {removing === f.id
                      ? <i className="ri-loader-4-line animate-spin text-xs text-foreground-500"></i>
                      : <i className="ri-heart-3-fill text-red-500 text-sm"></i>}
                  </button>
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <p className="text-sm font-semibold text-foreground-900 line-clamp-2">{f.label || 'Élément enregistré'}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {meta.boutiqueName && (
                      <span className="text-[11px] text-foreground-500 truncate flex items-center gap-0.5">
                        <i className="ri-store-2-line"></i>{meta.boutiqueName}
                      </span>
                    )}
                    {meta.ville && (
                      <span className="text-[11px] text-foreground-500 flex items-center gap-0.5">
                        <i className="ri-map-pin-line"></i>{meta.ville}
                      </span>
                    )}
                  </div>
                  {price && (
                    <p className="text-sm font-bold text-primary-600 mt-2 whitespace-nowrap">{price}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}